import { useCallback, useEffect, useState } from "react"

import { api, type SituacaoDosAvisos } from "../api"
import { Alert, AlertDescription, AlertTitle } from "../componentes/Alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../componentes/AlertDialog"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { ComDica } from "../componentes/ComDica"
import * as Icones from "../componentes/Icones"
import { Numero, Numeros } from "../componentes/Numeros"
import { Bloco, CarregandoNaSecao, FalhaNaSecao } from "../componentes/Secao"
import { Selo } from "../componentes/Selo"
import { toast } from "../componentes/Toast"
import { dataHora, inteiro, plural } from "../formato"

const CANAL_NA_TELA: Record<string, { nome: string; Icone: Icones.Icone; frase: string; abrirTeste: string }> = {
  sistema: { nome: "No sistema", Icone: Icones.Sino, frase: "no sistema", abrirTeste: "Abrir caixa de teste" },
  email: { nome: "E-mail", Icone: Icones.Enviar, frase: "e-mail", abrirTeste: "Abrir caixa de teste" },
  whatsapp: { nome: "WhatsApp", Icone: Icones.Canal, frase: "WhatsApp", abrirTeste: "Abrir conversas de teste" },
}

const BOTAO_DE_ACAO = "border-primary bg-card text-primary hover:bg-(--azul-50) hover:text-primary"

const mensagem = mensagemDeRecusa

export function CanaisDeAviso() {
  const [situacao, definirSituacao] = useState<SituacaoDosAvisos | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [testando, definirTestando] = useState<string | null>(null)
  const [processando, definirProcessando] = useState(false)
  const [confirmando, definirConfirmando] = useState(false)
  const [reiniciando, definirReiniciando] = useState(false)
  const [confirmandoReinicio, definirConfirmandoReinicio] = useState(false)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .situacaoDosAvisos()
      .then(definirSituacao)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  const processar = async () => {
    definirProcessando(true)
    try {
      const r = await api.processarAvisos()
      toast.success(plural(r.avisos_novos, "aviso novo", "avisos novos"), {
        description: [
          plural(r.enviadas, "enviado", "enviados"),
          plural(r.falhas, "falha", "falhas"),
          `${inteiro(r.nao_configuradas)} sem canal`,
          `${inteiro(r.sem_destino)} sem destino`,
        ].join(" · "),
      })
      carregar()
      window.dispatchEvent(new CustomEvent("avisos-mudaram"))
    } catch (erro: unknown) {
      toast.error(mensagem(erro))
    } finally {
      definirProcessando(false)
    }
  }

  const reiniciar = async () => {
    definirReiniciando(true)
    try {
      const r = await api.reiniciarAvisos()
      toast.success(plural(r.avisos_apagados, "aviso apagado", "avisos apagados"), {
        description: "O próximo Processar agora gera e manda tudo de novo para a caixa de teste.",
      })
      carregar()
      window.dispatchEvent(new CustomEvent("avisos-mudaram"))
    } catch (erro: unknown) {
      toast.error(mensagem(erro))
    } finally {
      definirReiniciando(false)
    }
  }

  const testar = async (canal: string) => {
    const frase = CANAL_NA_TELA[canal]?.frase ?? canal
    definirTestando(canal)
    try {
      const r = await api.testarCanal(canal)
      if (r.enviado) toast.success(`Teste por ${frase} enviado para ${r.destino ?? "—"}`)
      else toast.error(`Teste por ${frase} não saiu: ${r.resultado}${r.detalhe ? ` — ${r.detalhe}` : ""}`)
    } catch (erro: unknown) {
      toast.error(mensagem(erro))
    } finally {
      definirTestando(null)
    }
  }

  const carregando = (
    <>
      {falha !== null ? <FalhaNaSecao mensagem={falha} aoTentar={carregar} /> : <CarregandoNaSecao />}
    </>
  )
  const contagem = (chave: string) => inteiro(situacao?.por_resultado[chave] ?? 0)

  return (
    <div className="flex flex-col divide-y divide-(--borda)">
      <Bloco
        titulo="Canais de envio"
        dica="Por onde os avisos chegam a quem fiscaliza. No sistema, eles aparecem sempre na caixa de avisos; e-mail e WhatsApp dependem da configuração feita pelo setor de TI. O teste manda uma mensagem só para você."
      >
        {situacao === null ? (
          carregando
        ) : (
          <div className="divide-y divide-(--borda) rounded-md border border-(--borda)">
            {situacao.canais.map((c) => {
              const canal = CANAL_NA_TELA[c.canal] ?? { nome: c.canal, Icone: Icones.Sino, frase: c.canal, abrirTeste: "Abrir caixa de teste" }
              return (
                <div
                  key={c.canal}
                  data-slot="linha-do-canal"
                  className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-(--tinta) sm:w-48">
                    <span className="flex text-(--tinta-2)">
                      <canal.Icone />
                    </span>
                    {canal.nome}
                  </div>
                  <div className="flex flex-1 flex-wrap items-center gap-3 text-sm">
                    <Selo situacao={c.configurado ? "configurado" : "nao_configurado"}>
                      {c.caixa_de_teste ? "configurado • Teste" : undefined}
                    </Selo>
                    {c.configurado ? (
                      <span className="text-(--tinta-2)">{c.caixa_de_teste ? "As mensagens ficam na caixa de teste" : "Pronto para enviar"}</span>
                    ) : (
                      <span className="text-(--tinta-2)">
                        <ComDica dica="Falta a configuração do envio neste sistema. O setor de TI sabe o que preencher">
                          Peça ao setor de TI para configurar este canal
                        </ComDica>
                      </span>
                    )}
                  </div>
                  {c.caixa_de_teste && (
                    <Button asChild variant="outline" size="sm" className={BOTAO_DE_ACAO}>
                      <a href={c.caixa_de_teste} target="_blank" rel="noreferrer">
                        <Icones.Abrir />
                        {canal.abrirTeste}
                      </a>
                    </Button>
                  )}
                  {c.canal !== "sistema" && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={BOTAO_DE_ACAO}
                      disabled={!c.configurado || testando !== null}
                      title={!c.configurado ? "Falta configurar o canal" : undefined}
                      onClick={() => void testar(c.canal)}
                    >
                      <Icones.Enviar />
                      {testando === c.canal ? "Testando..." : "Testar comigo"}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Bloco>

      <Bloco
        titulo="Envio dos avisos"
        dica="A rodada diária gera os avisos dos prazos e os manda pelos canais configurados. Processar agora faz a mesma coisa na hora, para a entidade em que você está."
        acoes={
          <>
            {situacao?.pode_reiniciar && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={BOTAO_DE_ACAO}
                disabled={reiniciando || processando}
                onClick={() => definirConfirmandoReinicio(true)}
              >
                <Icones.Reabrir />
                {reiniciando ? "Reiniciando..." : "Reiniciar avisos"}
              </Button>
            )}
            <Button type="button" size="sm" disabled={situacao === null || processando || reiniciando} onClick={() => definirConfirmando(true)}>
              <Icones.Recalcular />
              {processando ? "Processando..." : "Processar agora"}
            </Button>
          </>
        }
      >
        {situacao === null ? (
          falha !== null ? (
            <p className="m-0 text-sm text-(--tinta-2)">Os números aparecem quando a consulta acima responder.</p>
          ) : (
            <CarregandoNaSecao />
          )
        ) : (
          <>
            <Numeros>
              <Numero rotulo="Pendentes" valor={contagem("pendente")} nota="esperando a vez" />
              <Numero rotulo="Enviados" valor={contagem("enviado")} nota="por e-mail ou WhatsApp" />
              <Numero rotulo="Falharam" valor={contagem("falhou")} nota="o canal recusou" />
              <Numero rotulo="Sem canal" valor={contagem("nao_configurado")} nota="canal não configurado" />
              <Numero rotulo="Sem destino" valor={contagem("sem_destino")} nota="sem e-mail ou telefone" />
            </Numeros>
            <p className="m-0 text-sm text-(--tinta-2)">
              {situacao.ultima_execucao === null ? "Nunca rodou." : `Último envio: ${dataHora(situacao.ultima_execucao)}`}
            </p>
            {situacao.ultima_execucao_falhou && (
              <Alert variant="destructive">
                <Icones.Erro />
                <AlertTitle>O último envio falhou</AlertTitle>
                <AlertDescription>Peça ao setor de TI para verificar os canais e o relógio do sistema.</AlertDescription>
              </Alert>
            )}
          </>
        )}
      </Bloco>

      <AlertDialog open={confirmando} onOpenChange={definirConfirmando}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Processar os avisos agora?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              O sistema gera os avisos dos prazos e os manda de verdade, pelos canais configurados, para quem fiscaliza a entidade em que você está.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                onClick={() => {
                  definirConfirmando(false)
                  void processar()
                }}
              >
                <Icones.Recalcular />
                Processar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmandoReinicio} onOpenChange={definirConfirmandoReinicio}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reiniciar os avisos?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              {situacao?.todas_as_entidades ? "Os avisos de todas as entidades" : "Os avisos desta entidade"} são apagados, inclusive os da caixa de avisos de cada pessoa. O próximo Processar agora gera tudo de novo e manda as mensagens para a caixa de teste.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                <Icones.Fechar />
                Cancelar
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  definirConfirmandoReinicio(false)
                  void reiniciar()
                }}
              >
                <Icones.Reabrir />
                Reiniciar
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
